import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Menu, X, ArrowUpRight } from "lucide-react";
import LanguageToggle from "./LanguageToggle";
import ThemeToggle from "./ThemeToggle";
import ConnectWalletButton from "./ConnectWalletButton";
import { useEffect, useState } from "react";

const Header = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  const links = [
    { to: "/", label: t("nav.markets") },
    { to: "/portfolio", label: t("nav.portfolio") },
    { to: "/admin", label: t("nav.admin") },
    { to: "/about", label: t("nav.about") },
  ];

  const isActive = (path: string) => location.pathname === path;

  useEffect(() => {
    function onScroll() {
      setScrolled(window.scrollY > 8);
    }

    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  return (
    <header
      className={`sticky top-0 z-50 w-full border-b transition-all duration-200 ${
        scrolled
          ? "border-border/80 bg-background/95 shadow-sm backdrop-blur"
          : "border-transparent bg-background/92 backdrop-blur"
      }`}
    >
      <div className="mx-auto max-w-7xl px-4">
        <div className="flex h-20 items-center justify-between gap-4">
          <Link to="/" className="flex shrink-0 items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-full border border-primary text-primary">
              <ArrowUpRight className="h-5 w-5" />
            </div>

            <span className="text-[18px] font-bold uppercase tracking-[0.12em] text-foreground">
              ORION
            </span>
          </Link>

          <nav className="hidden items-center gap-8 md:flex">
            {links.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className={`relative py-2 text-[15px] font-medium transition-colors ${
                  isActive(link.to)
                    ? "text-primary"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                {link.label}
                {isActive(link.to) && (
                  <span className="absolute inset-x-0 -bottom-[7px] h-[2px] rounded-full bg-primary" />
                )}
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <ThemeToggle />
            <LanguageToggle />

            <div className="hidden md:block">
              <ConnectWalletButton />
            </div>

            <button
              type="button"
              className="rounded-xl border border-border/70 p-2 text-foreground transition hover:bg-muted/50 md:hidden"
              onClick={() => setMobileOpen((prev) => !prev)}
              aria-label="Toggle menu"
            >
              {mobileOpen ? (
                <X className="h-5 w-5" />
              ) : (
                <Menu className="h-5 w-5" />
              )}
            </button>
          </div>
        </div>

        {mobileOpen && (
          <div className="border-t border-border/70 pb-4 pt-3 md:hidden">
            <nav className="space-y-1">
              {links.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  className={`block rounded-xl px-3 py-3 text-[14px] font-medium transition ${
                    isActive(link.to)
                      ? "bg-accent text-primary"
                      : "text-muted-foreground hover:bg-muted/40 hover:text-foreground"
                  }`}
                >
                  {link.label}
                </Link>
              ))}
            </nav>

            <div className="mt-3">
              <ConnectWalletButton />
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;